import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const ToastContext = createContext(null);

const TOAST_TTL_MS = 4000;

const TYPE_STYLES = {
  success: { bg: "var(--healthy-dim)", color: "var(--healthy)", border: "var(--healthy)" },
  error: { bg: "var(--critical-dim)", color: "var(--critical)", border: "var(--critical)" },
  warning: { bg: "var(--warning-dim)", color: "var(--warning)", border: "var(--warning)" },
  info: { bg: "rgba(83, 82, 237, 0.12)", color: "var(--info)", border: "var(--info)" },
};

let nextToastId = 0;

function ToastItem({ toast, onDismiss }) {
  const style = TYPE_STYLES[toast.type] || TYPE_STYLES.info;

  return (
    <div
      role="status"
      style={{
        padding: "12px 16px",
        borderRadius: "6px",
        fontSize: "14px",
        lineHeight: 1.4,
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        maxWidth: "360px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
        <span>{toast.message}</span>
        <button
          type="button"
          onClick={() => onDismiss(toast.id)}
          aria-label="Dismiss"
          style={{
            background: "transparent",
            border: "none",
            color: "inherit",
            cursor: "pointer",
            fontSize: "16px",
            lineHeight: 1,
            padding: 0,
            opacity: 0.8,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message, type = "info") => {
      const id = ++nextToastId;
      const toast = { id, message: String(message || ""), type };
      setToasts((prev) => [...prev, toast]);
      const timer = setTimeout(() => dismiss(id), TOAST_TTL_MS);
      timersRef.current.set(id, timer);
    },
    [dismiss],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const value = { show, dismiss };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 200,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            pointerEvents: "none",
          }}
        >
          {toasts.map((toast) => (
            <div key={toast.id} style={{ pointerEvents: "auto" }}>
              <ToastItem toast={toast} onDismiss={dismiss} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
